import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ImageDropZone from "./ImageDropZone";
import { compressToWebP, withUploadTimeout } from "@/lib/imageCompression";

const AdminFleet = () => {
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [edits, setEdits] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    const { data } = await supabase.from("fleet_vehicles").select("*").order("slot", { ascending: true });
    setVehicles(data || []);
    const e: Record<string, any> = {};
    data?.forEach((v) => {
      e[v.id] = { name: v.name, capacity_passengers: v.capacity_passengers, capacity_luggage: v.capacity_luggage, description: v.description || "", price: v.price || "" };
    });
    setEdits(e);
  };

  useEffect(() => { load(); }, []);

  const save = async (v: any) => {
    setSaving(v.id);
    const edit = edits[v.id];
    const { error } = await supabase.from("fleet_vehicles").update({
      name: edit.name,
      capacity_passengers: edit.capacity_passengers,
      capacity_luggage: edit.capacity_luggage,
      description: edit.description || null,
      price: edit.price || null,
    }).eq("id", v.id);
    if (error) toast.error("Erreur: " + error.message);
    else toast.success(`${edit.name} mis à jour !`);
    setSaving(null);
  };

  const uploadImage = async (v: any, file: File) => {
    const isPng = /png/i.test(file.type) || /\.png$/i.test(file.name);
    const uploadFile = isPng ? file : await compressToWebP(file, { maxSizeMB: 0.5 });
    const ext = isPng ? "png" : "webp";
    const path = `fleet-${v.slot}.${ext}`;
    await supabase.storage.from("site-images").remove([`fleet-${v.slot}.png`, `fleet-${v.slot}.webp`, `fleet-${v.slot}.jpg`, `fleet-${v.slot}.jpeg`]).catch(() => {});
    const { error: uploadError } = await withUploadTimeout(
      supabase.storage.from("site-images").upload(path, uploadFile, { upsert: true, contentType: uploadFile.type })
    );
    if (uploadError) throw new Error("Erreur upload : " + uploadError.message);
    const { data: urlData } = supabase.storage.from("site-images").getPublicUrl(path);
    const url = urlData.publicUrl + "?t=" + Date.now();
    const { error: dbError } = await supabase.from("fleet_vehicles").update({ image_url: url }).eq("id", v.id);
    if (dbError) throw new Error("Erreur DB : " + dbError.message);
    toast.success("Image mise à jour !");
    load();
  };

  const updateField = (id: string, field: string, value: string) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {vehicles.map((v) => (
        <Card key={v.id} className="border-border hover:border-primary/30 transition-colors">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center justify-between">
              <span>Slot {v.slot}</span>
              <span className="text-xs font-body text-muted-foreground">#{v.slot}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ImageDropZone
              currentUrl={v.image_url}
              onUpload={(file) => uploadImage(v, file)}
              accept="image/png"
              label="PNG transparent recommandé"
              height="h-36"
            />

            <div>
              <label className="block font-body text-xs text-muted-foreground mb-1">Nom du véhicule</label>
              <Input value={edits[v.id]?.name || ""} onChange={(e) => updateField(v.id, "name", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Passagers</label>
                <Input value={edits[v.id]?.capacity_passengers || ""} onChange={(e) => updateField(v.id, "capacity_passengers", e.target.value)} />
              </div>
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Bagages</label>
                <Input value={edits[v.id]?.capacity_luggage || ""} onChange={(e) => updateField(v.id, "capacity_luggage", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="block font-body text-xs text-muted-foreground mb-1">Prix (optionnel)</label>
              <Input value={edits[v.id]?.price || ""} onChange={(e) => updateField(v.id, "price", e.target.value)} placeholder="ex: 500 DH" />
            </div>
            <div>
              <label className="block font-body text-xs text-muted-foreground mb-1">Description</label>
              <Input value={edits[v.id]?.description || ""} onChange={(e) => updateField(v.id, "description", e.target.value)} />
            </div>

            <Button onClick={() => save(v)} disabled={saving === v.id} className="w-full bg-gradient-gold text-primary-foreground" size="sm">
              {saving === v.id ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AdminFleet;
